import React, { useRef } from "react";
import { useSearchParams } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';

import Pagination, { PaginationInterface } from "../Pagination";
import { Table as TableAntd, TableProps } from "antd";
import type { InputRef } from 'antd';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { Button, Input, Space } from "antd";
import './style.scss';

interface TableInterface {
  loading?: TableProps<any>['loading'];
  rowKey: TableProps<any>['rowKey'];
  columns: TableProps<any>['columns'];
  dataSource: TableProps<any>['dataSource'];
  expandable?: TableProps<any>['expandable'];
  rowSelection?: TableProps<any>['rowSelection'];
  pagination?: PaginationInterface;
  search_keys?: string[];
  sort_keys?: string[];
}

const Table = (
  { loading, columns, dataSource, expandable, pagination, rowSelection, rowKey = 'id', search_keys, sort_keys }: TableInterface
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTerm = searchParams.get('term') || '';
  const urlSearchBy = searchParams.get('search_by') || '';
  const urlOrderBy = searchParams.get('order_by') || '';
  const urlOrder = searchParams.get('order') || '';
  const searchInput = useRef<InputRef>(null);

  // --- Column search ---

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string,
  ) => {
    confirm();
    searchParams.set('term', selectedKeys[0]);
    searchParams.set('search_by', dataIndex);
    searchParams.delete('page');
    setSearchParams(searchParams, { replace: true });
  };

  const handleReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void) => {
    clearFilters();
    confirm();
    searchParams.delete('term');
    searchParams.delete('search_by');
    searchParams.delete('page');
    setSearchParams(searchParams, { replace: true });
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filteredValue: urlSearchBy === dataIndex && urlTerm ? [urlTerm] : null,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={`${selectedKeys[0] || ''}`}
          onChange={(e: any) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => close()}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // --- Column sort ---

  const getSortOrder = (key: string): 'ascend' | 'descend' | null => {
    if (urlOrderBy !== key) return null;
    return urlOrder === 'asc' ? 'ascend' : urlOrder === 'desc' ? 'descend' : null;
  };

  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (!sort_keys) return;
    const key = sorter.columnKey || sorter.field;
    if (sorter.order) {
      searchParams.set('order_by', key);
      searchParams.set('order', sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      searchParams.delete('order_by');
      searchParams.delete('order');
    }
    searchParams.delete('page');
    setSearchParams(searchParams, { replace: true });
  };

  // --- Enhance columns ---

  const enhancedColumns = columns
    ? columns.map((col: any) => {
        let enhanced = col;
        if (search_keys?.includes(col.key as string)) {
          enhanced = { ...enhanced, ...getColumnSearchProps(col.key as string) };
        }
        if (sort_keys?.includes(col.key as string)) {
          enhanced = {
            ...enhanced,
            sorter: true,
            sortOrder: getSortOrder(col.key as string),
          };
        }
        return enhanced;
      })
    : columns;

  return (
    <>
      <TableAntd
        className='v-table'
        rowKey={rowKey}
        loading={loading}
        columns={enhancedColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        pagination={false}
        expandable={expandable}
        onChange={sort_keys ? handleTableChange : undefined}
      />

      {pagination && <Pagination {...pagination} />}
    </>
  )
}

Table.EXPAND_COLUMN = TableAntd.EXPAND_COLUMN;

export default Table;
